import { Injectable, isDevMode } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { SimpleShipment } from '../domain/SimpleShipment';
import { Shipment, ShipmentPrice, ShipmentPriceV2 } from '../domain/Shipment';
import { formatDate } from '@angular/common';
import { SignalrService } from './signalr.service';
import { HubConnection } from '@microsoft/signalr';
import { BehaviorSubject, map, Observable, of, shareReplay, startWith, Subject, switchMap, throttleTime } from 'rxjs';
import { ClientService } from './client.service';
import { Colli } from '../domain/Colli';
import { TenantService } from './tenant.service';

@Injectable({
  providedIn: 'root',
})
export class ShipmentService {
  hubConnection: HubConnection;

  todaysShipmentsCache = new BehaviorSubject<SimpleShipment[]>([]); // in mem storeage NOT a chache thing 
  notSubmittedShipmentsCache = new BehaviorSubject<SimpleShipment[]>([]); // in mem storeage NOT a cache thing...
  currentProcessingShipmentTrackingId = Guid.newGuid();
  notSubmittedShipmentsCount$ = this.notSubmittedShipmentsCache.pipe(map(shipments => shipments.length));
  archiveResultSet$ = new BehaviorSubject<ArchiveSearchObject[]>(new Array<ArchiveSearchObject>());
  private refreshTodaysShipments = new Subject<void>();
  private refreshNotSubmittedShipments = new Subject<void>();

  notSubmittedShipments$ = new BehaviorSubject<SimpleShipment[]>(new Array<SimpleShipment>());

  constructor(private httpClient: HttpClient, private signalr: SignalrService, private clientService: ClientService, private tenantService: TenantService) {
    this.ensureHubConnection();

    this.hubConnection.onreconnected(() => {
      this.forceRefreshShipmentsCache();
    });

    this.fetchTodaysShipments().subscribe((x) => {
      this.todaysShipmentsCache.next(x)
    });

    this.refreshNotSubmittedShipments.pipe(throttleTime(3000), switchMap(() => this.fetchNotSubmittedShipmentsV3())).subscribe((shipments: SimpleShipment[]) => {
      this.notSubmittedShipmentsCache.next(shipments);
    });

    this.setupTodaysShipmentsSignalR();
    this.setupNotSubmittedShipmentsSignalR();

    this.refreshTodaysShipments.pipe(throttleTime(3000), switchMap(() => this.fetchTodaysShipments())).subscribe((shipments: SimpleShipment[]) => {
      this.todaysShipmentsCache.next(shipments);
    });
  }

  private ensureHubConnection(): HubConnection {
    if (!this.hubConnection || this.hubConnection.state !== 'Connected') {
      if (this.hubConnection && this.hubConnection.state === 'Connecting') {
        return this.hubConnection;
      }
      this.hubConnection = this.signalr.CreateConnection('shipment');
      this.hubConnection.start().then((x: any) => { console.log("SignilR createconection after start : ", x) }).catch((err) => {
        console.error(err);
      });
    }
    return this.hubConnection;
  }

  updateShipmentStatus(shipmentId: any, status: any, eventDate: any) {
    let updateCommand = {
      shipmentId: shipmentId,
      newStatus: status,
      eventDate: eventDate,
    };
    return this.httpClient.post(`${environment.apiUrl}/shipments/updateStatus`, updateCommand);
  }

  updateShipmentDetails(shipment: Shipment) {
    let updateCommand = {
      updateShipmentDetails: {
        shipmentId: shipment.id,
        reference1: shipment.reference1,
        reference2: shipment.reference2,
        reference3: shipment.reference3,
        note: shipment.note,
        driverNote: shipment.driverNote,
      },
    };
    return this.httpClient.post(`${environment.apiUrl}/shipments/updateDetails`, updateCommand);
  }

  updateShipmentExchangePallets(shipment: any) {
    let updateCommand = {
      updateShipmentExchangePallets: {
        shipmentId: shipment.id,
        fullExchangePallets: shipment.fullExchangePallets.quantity != null ? shipment.fullExchangePallets : null,
        halfExchangePallets: shipment.halfExchangePallets.quantity != null ? shipment.halfExchangePallets : null,
        quarterExchangePallets: shipment.quarterExchangePallets.quantity != null ? shipment.quarterExchangePallets : null,
      },
    };
    return this.httpClient.post(`${environment.apiUrl}/shipments/updateExchangePallets`, updateCommand);
  }

  updateShipmentDangerousGoods(shipment: any) {
    if (shipment.dangerousGoods && shipment.dangerousGoods.length > 0) {
      // Fix model and remove this code
      for (let i = 0; i < shipment.dangerousGoods.length; i++) {
        if (shipment.dangerousGoods[i].nameAndDescription) {
          shipment.dangerousGoods[i].name = shipment.dangerousGoods[i].nameAndDescription;
        } else {
          shipment.dangerousGoods[i].name = '';
        }
      }
    }
    let updateDangerousGoodsCommand = {
      updateShipmentDangerousGoods: {
        shipmentId: shipment.id,
        dangerousGoods: shipment.dangerousGoods,
      },
    };
    return this.httpClient.post(`${environment.apiUrl}/shipments/updateDangerousGoods`, updateDangerousGoodsCommand);
  }

  updateShipmentServices(shipment: any, insureanceAmount = 0) {
    var updateShipmentServicesCommand = {
      updateShipmentServices: {
        shipmentId: shipment.id,
        services: shipment.services,
        insuranceAmount: insureanceAmount
      },
    };
    return this.httpClient.post(`${environment.apiUrl}/shipments/updateServices`, updateShipmentServicesCommand);
  }

  updateShipmentDates(shipment: any) {
    this.sanitizeTimeOnlyFields(shipment);
    var updateShipmentDateCommand = {
      updateShipmentDates: {
        shipmentId: shipment.id,
        pickupDate: formatDate(shipment.pickupDate, 'yyyy-MM-dd', 'da-DK'),
        deliveryDate: formatDate(shipment.deliveryDate, 'yyyy-MM-dd', 'da-DK'),
        pickupTimeStart: shipment.pickupTimeStart,
        pickupTimeEnd: shipment.pickupTimeEnd,
        deliveryTimeStart: shipment.deliveryTimeStart,
        deliveryTimeEnd: shipment.deliveryTimeEnd,
      },
    };
    return this.httpClient.post(`${environment.apiUrl}/e/shipments/updateDates`, updateShipmentDateCommand);
  }

  updateShipmentPickupAndDeliveryDates(shipmentId: any, pickupDate: any, deliveryDate: any) {
    var updateShipmentDateCommand = {
      updateShipmentDates: {
        shipmentId: shipmentId,
        pickupDate: pickupDate,
        deliveryDate: deliveryDate,
      },
    };
    return this.httpClient.post(`${environment.apiUrl}/e/shipments/updateDates`, updateShipmentDateCommand);
  }

  private sanitizeTimeOnlyFields(shipment: any) {
    const timeFields = ['pickupTimeStart', 'pickupTimeEnd', 'deliveryTimeStart', 'deliveryTimeEnd'];
    timeFields.forEach((field) => {
      if (shipment[field] && shipment[field].includes('.')) {
        shipment[field] = shipment[field].replace('.', ':');
      }
      if (shipment[field] && shipment[field].length === 5) {
        shipment[field] = `${shipment[field]}:00`;
      }
    });
  }

  fetchTodaysShipments(): Observable<SimpleShipment[]> {
    return this.fetchTodaysShipmentsV3();
    let queryParams = new HttpParams();
    queryParams = queryParams.append('date', new Date().toISOString());
    queryParams = queryParams.append('status', 'Ready');
    return this.httpClient.get<SimpleShipment[]>(`${environment.apiUrl}/shipments/simple`, { params: queryParams });
  }

  fetchTodaysShipmentsV3(): Observable<SimpleShipment[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('ToDate', new Date().toISOString().split('T')[0]);
    queryParams = queryParams.append('FromDate', new Date().toISOString().split('T')[0]);
    queryParams = queryParams.append('Status', 'Ready');
    queryParams = queryParams.append('ClientId', this.clientService.clientId);

    return this.httpClient.get<SimpleShipment[]>(`${environment.apiUrl}/e/shipments/clientV3`, { params: queryParams });
  }

  // creates a hubconnection and sets a listner up on "ShipmentReady"
  private setupTodaysShipmentsSignalR() {
    const connection = this.ensureHubConnection();

    connection.on('shipment-ready', () => {
      this.refreshTodaysShipments.next();
    });
  }

  private setupNotSubmittedShipmentsSignalR() {
    const connection = this.ensureHubConnection();

    connection.on('shipment-created', () => {
      this.refreshNotSubmittedShipments.next();
    });
    connection.on('shipment-ready', () => {
      this.refreshNotSubmittedShipments.next();
    });
  }

  getTodaysShipments() {
    return this.todaysShipmentsCache.asObservable();
  }

  getTodaysShipmentsV3(): Observable<SimpleShipment[]> {
    return this.refreshTodaysShipments.pipe(
      startWith(undefined),
      switchMap(() => this.fetchTodaysShipmentsV3()),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private fetchNotSubmittedShipments(): Observable<SimpleShipment[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('status', 'Booked');

    return this.httpClient.get<SimpleShipment[]>(`${environment.apiUrl}/shipments/simple`, { params: queryParams });
  }

  private fetchNotSubmittedShipmentsV3(): Observable<SimpleShipment[]> {
    const params = new HttpParams().set('Status', 'Booked').set('ClientId', this.clientService.clientId);

    return this.httpClient.get<SimpleShipment[]>(`${environment.apiUrl}/e/shipments/clientV3`, { params });
  }

  getNotSubmittedShipmentsV3() {
    return this.refreshNotSubmittedShipments.pipe(
      startWith(undefined),
      switchMap(() => this.fetchNotSubmittedShipmentsV3()), shareReplay({ bufferSize: 1, refCount: true })
    );

    // const params = new HttpParams().set('Status', 'Booked').set('ClientId', this.clientService.clientId);
    // return this.httpClient.get<SimpleShipment[]>(`${environment.apiUrl}/e/shipments/clientV3`, { params }
    // );
  }


  getNotSubmittedShipments() {
    return this.notSubmittedShipmentsCache.asObservable();
  }

  forceRefreshShipmentsCache() {
    this.refreshNotSubmittedShipments.next();
    this.refreshTodaysShipments.next();
    // this.getNotSubmittedShipmentsV3();
  }

  deleteShipment(shipmentId: any) {
    let deleteShipmentCommand = {
      shipmentId: shipmentId,
      newStatus: 'Deleted',
    };
    return this.httpClient.post(`${environment.apiUrl}/shipments/updateStatus`, deleteShipmentCommand);
  }

  /**
   * this calls a endpoint for marking a shipment as ready.
   * @returns result of a post opreation
   */
  markAsReady(shipmentId, message, SendMail = true) {
    let command: MarkShipmentAsReadyCommand = { Message: message, ShipmentId: shipmentId, SendMail: SendMail } as MarkShipmentAsReadyCommand;
    // return of({ message: {} as SimpleShipment });
    return this.httpClient.post(`${environment.apiUrl}/e/shipments/ready`, command);
  }

  MarkAsReadyWithGroup(shipmentIds: string[], message: string) {
    let command = { Message: message, shipmentIds: shipmentIds, SendMail: true };
    return this.httpClient.post(`${environment.apiUrl}/e/shipments/markgroupshipmentasready`, command);
  }

  getFullShipment(shipmentId: string): Observable<Shipment> {
    // if in localhost mode
    if (false && isDevMode()) {
      const mockData = {
        id: "01000000-6563-da17-748c-08dd7bf1d0da",
        bookingDate: "2025-04-15T07:47:47.78+00:00",
        readyDate: null,
        ediId: null,
        bookingSource: "Web2",
        deliveryAddress: null,
        pickupAddress: null,
        recipientAddress: {
          addressId: "01000000-6563-da17-e53d-08dd7bf1d0d8",
          name: "adr tester",
          contactPerson: null,
          contactPhone: null,
          contactEmail: null,
          street: "Grimstrupvej 1",
          zipCode: "3310",
          city: "Ølsted",
          countryCode: "DK",
          longitude: 12.08815915,
          latitude: 55.90877555,
          validatedBy: ""
        },
        senderAddress: {
          addressId: "01000000-6563-da17-e582-08dd7bf1d0d8",
          name: "Alice TMS Test",
          contactPerson: null,
          contactPhone: "70505909",
          contactEmail: "support@alicetms.dk",
          street: "Alfarvejen 61",
          zipCode: "4320",
          city: "Osted",
          countryCode: "DK",
          longitude: 11.954290390014648,
          latitude: 55.558128356933594,
          validatedBy: ""
        },
        pickupDate: "2025-04-15",
        pickupTimeStart: null,
        pickupTimeEnd: null,
        deliveryDate: "2025-04-16",
        deliveryTimeStart: null,
        deliveryTimeEnd: null,
        reference1: null,
        reference2: null,
        reference3: null,
        note: "tester adr",
        driverNote: null,
        status: "Booked",
        waybillNo: "19075515",
        trackAndTrace: "190755151337133719",
        isPickup: false,
        services: [],
        dangerousGoods: [
          {
            shipmentId: "00000000-0000-0000-0000-000000000000",
            colliId: null,
            name: "BENZIN",
            unNumber: "1203",
            liquid: false,
            environmental: false,
            netWeightKg: 0.0,
            grossWeightKg: 2.0,
            volumeM3: 0.0,
            colliQuantity: 0,
            tunnelCode: null,
            class: "BENZIN",
            waybillString: "UN 1203 BENZIN, 3, PG II (D/E)",
            packageGroup: "II",
            transportCategory: "2",
            imdg: null,
            packaging: "P001 IBC02 R001"
          },
          {
            shipmentId: "00000000-0000-0000-0000-000000000000",
            colliId: null,
            name: "NITROGLYCERIN, OPLØSNING I ALKOHOL med højst 1% nitroglycerin",
            unNumber: "1204",
            liquid: false,
            environmental: false,
            netWeightKg: 0.0,
            grossWeightKg: 3.0,
            volumeM3: 0.0,
            colliQuantity: 0,
            tunnelCode: null,
            class: "NITROGLYCERIN, OPLØSNING I ALKOHOL med højst 1% nitroglycerin",
            waybillString: "UN 1204 NITROGLYCERIN OPLØSNING I ALKOHOL med højst 1% nitroglycerin, 3, PG II (B)",
            packageGroup: "II",
            transportCategory: "2",
            imdg: null,
            packaging: "P001 IBC02"
          }
        ],
        clientPayerId: null,
        accountId: null,
        carrier: "Internal",
        bookingType: null,
        parcelShopId: null,
        fullExchangePallets: {
          quantity: 0,
          deviation: 0
        },
        halfExchangePallets: {
          quantity: 0,
          deviation: 0
        },
        quarterExchangePallets: {
          quantity: 0,
          deviation: 0
        },
        collis: [
          {
            id: "01000000-6563-da17-1acd-08dd7bf1d0db",
            lineId: "01000000-6563-da17-ba93-08dd7bf1d0da",
            type: "1/1",
            description: "tester adr",
            barcodes: [],
            weight: 5.0,
            length: 0.0,
            width: 0.0,
            height: 0.0,
            loadMeter: 0.4,
            volume: 0.0,
            quantity: 1
          }
        ]
      } as any;
      return of(mockData);
    }

    let queryParams = new HttpParams().append('shipmentId', shipmentId);
    let postTest = this.httpClient.get<Shipment>(`${environment.apiUrl}/e/shipments/full/${shipmentId}`);

    // let postTest = this.httpClient.get<Shipment>(`${environment.apiUrl}/e/shipments/full`, {
    //   params: queryParams,
    // });
    // postTest.subscribe(x => {
    // })
    return postTest;
  }

  createShipment(shipment: Shipment) {
    if (shipment.recipientAddress.contactPerson && typeof shipment.recipientAddress.contactPerson === 'object' && 'name' in shipment.recipientAddress.contactPerson) {
      shipment.recipientAddress.contactPerson = shipment.recipientAddress.contactPerson.name;
    }

    return new Observable((subscriber) => {
      if (shipment.senderAddress && shipment.senderAddress.date) {
        var dateArray = shipment.senderAddress.date.toString().split('/');
        var convertedDate = new Date(dateArray[1] + '/' + dateArray[0] + '/' + dateArray[2]);
        shipment.pickupDate = formatDate(convertedDate, 'yyyy-MM-dd', 'en-US');
        if (shipment.senderAddress.timeStart) {
          shipment.pickupTimeStart = shipment.senderAddress.timeStart;
        }
        if (shipment.senderAddress.timeEnd) {
          shipment.pickupTimeEnd = shipment.senderAddress.timeEnd;
        }
      }

      if (shipment.Price != null && shipment.Price != undefined && shipment.Price.currency != null && shipment.Price.totalPrice > 0) {
        var tempPrice = shipment.Price as ShipmentPriceV2;
        shipment.Price = {
          totalPrice: shipment.Price.totalPrice,
          currency: tempPrice.currency,
          priceLines: tempPrice.lines,
          priceValid: tempPrice.priceValid
        } as ShipmentPrice
      } else {
        shipment.Price = null;
      }

      if (shipment.recipientAddress && shipment.recipientAddress.date) {
        var dateArray = shipment.recipientAddress.date.toString().split('/');
        var convertedDate = new Date(dateArray[1] + '/' + dateArray[0] + '/' + dateArray[2]);
        shipment.deliveryDate = formatDate(convertedDate, 'yyyy-MM-dd', 'en-US');
        if (shipment.recipientAddress.timeStart) {
          shipment.deliveryTimeStart = shipment.recipientAddress.timeStart;
        }
        if (shipment.recipientAddress.timeEnd) {
          shipment.deliveryTimeEnd = shipment.recipientAddress.timeEnd;
        }
      }

      shipment.services = this.CheckAfterServiceAltCodes(shipment.services, false)
      this.sanitizeTimeOnlyFields(shipment);
      this.currentProcessingShipmentTrackingId = Guid.newGuid();
      (shipment as any).trackingNumber = this.currentProcessingShipmentTrackingId;

      this.hubConnection.on('booking-success', (msg) => {
        if (this.currentProcessingShipmentTrackingId == msg.trackingNumber) {
          subscriber.next(msg);
          this.refreshTodaysShipments.next();
          subscriber.complete();
        }
      });

      this.hubConnection.on('booking-failure', (msg) => {
        if (this.currentProcessingShipmentTrackingId == msg.trackingNumber) {
          console.error(msg);
          subscriber.error(msg);
          subscriber.complete();
        }
      });


      this.hubConnection.invoke('BookShipment', shipment).catch((err) => {
        subscriber.error(err);
        subscriber.complete();
      });

    });
  }

  archiveSearch(query: ShipmentArchiveQuery) {
    let queryParams = new HttpParams();
    if (query.fromDate && typeof query.fromDate === 'string') {
      const dateArray = query.fromDate.split('/');
      const convertedDate = new Date(`${dateArray[1]}/${dateArray[0]}/${dateArray[2]}`);
      query.fromDate = this.formatDateToString(convertedDate);
    }

    if (query.toDate && typeof query.toDate === 'string') {
      const dateArray = query.toDate.split('/');
      const convertedDate = new Date(`${dateArray[1]}/${dateArray[0]}/${dateArray[2]}`);
      query.toDate = this.formatDateToString(convertedDate);
    }

    if (query.deliveryDate && typeof query.deliveryDate === 'string') {
      const dateArray = query.deliveryDate.split('/');
      const convertedDate = new Date(`${dateArray[1]}/${dateArray[0]}/${dateArray[2]}`);
      query.deliveryDate = this.formatDateToString(convertedDate);
    }
    Object.entries(query).forEach(([key, value]) => {
      if (value) {
        queryParams = queryParams.append(key, value);
      }
    });

    return this.httpClient.get(`${environment.apiUrl}/shipments/archive`, {
      params: queryParams,
    });
  }

  formatDateToString(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDateStringToDate(date: any): string {
    const dateArray = date.split('/');
    const convertedDate = new Date(dateArray[1] + '/' + dateArray[0] + '/' + dateArray[2]);
    return formatDate(convertedDate, 'yyyy-MM-dd', 'en-US');
  }

  formatDateOnlyStringToDate(date: any): string {
    const dateArray = date.split('-');
    const convertedDate = new Date(dateArray[0] + '/' + dateArray[1] + '/' + dateArray[2]);
    return formatDate(convertedDate, 'yyyy-MM-dd', 'en-US');
  }

  calculatePrice(shipment: Shipment, CheckPriceFromBookedShipments: boolean = false) {
    const postShipment = {
      clientId: this.clientService.clientId,
      deliveryDate: CheckPriceFromBookedShipments ? this.formatDateOnlyStringToDate(shipment.deliveryDate) : this.formatDateStringToDate(shipment.recipientAddress.date),
      pickupDate: CheckPriceFromBookedShipments ? this.formatDateOnlyStringToDate(shipment.pickupDate) : this.formatDateStringToDate(shipment.senderAddress.date),
      pickupTimeStart: shipment.senderAddress.timeStart,
      pickupTimeEnd: shipment.senderAddress.timeEnd,
      deliveryTimeStart: shipment.recipientAddress.timeStart,
      deliveryTimeEnd: shipment.recipientAddress.timeEnd,
      services: this.CheckAfterServiceAltCodes(shipment.services, true),
      bookingDate: new Date(),
      collis: this.getCollies(shipment.collis),
      pickupAddress: {
        name: shipment.senderAddress.name,
        zipCode: shipment.senderAddress.zipCode,
        address: shipment.senderAddress.street,
        countryCode: shipment.senderAddress.countryCode,
      },
      deliveryAddress: {
        name: shipment.recipientAddress.name,
        zipCode: shipment.recipientAddress.zipCode,
        address: shipment.recipientAddress.street,
        countryCode: shipment.recipientAddress.countryCode,
      },
    };

    return this.httpClient.post<any>(`${environment.apiUrl}/e/shipments/price`, postShipment);
  }

  /**
   * this has a job, that job is to take in a guid and find the corrernt service from tenant.
   * @param services contains a list of guids for eachservice
   * @returns a list of erp codes/ old online-book service code
   */
  CheckAfterServiceAltCodes(services: any[], usePriceLookUpKey: boolean = false): string[] {
    let result = new Array<string>();
    if (services && services.length > 0) {
      (services as string[]).forEach(x => {
        let temp = this.tenantService.services$.getValue().find(y => y.id.toLowerCase() == x.toLowerCase() || y.code.toLowerCase() == x.toLowerCase())
        if (temp != null || temp != undefined) {
          result.push(usePriceLookUpKey ? (temp.priceLookupKey ?? temp.code) : temp.code);
        }
        else {
          temp = this.clientService.clientServices.find(y => y.id.toLowerCase() == x.toLowerCase() || y.code.toLowerCase() == x.toLowerCase());
          if (temp != null || temp != undefined) {
            result.push(usePriceLookUpKey ? (temp.priceLookupKey ?? temp.code) : temp.code);
          } else {
            result.push(x)
          }
        }
      })
    }
    else {
      return result;
    }
    return result;
  }

  getCollies(collies: Colli[]): any[] {
    let cols = new Array()
    for (let j = 0; j < collies.length; j++) {
      let colli = collies[j]
      for (let i = 0; i < colli.quantity; i++) {
        let col = {
          weight: Math.round(((colli.weight ?? 0) / colli.quantity) * 100) / 100,
          volume: Math.round((+(colli.volume ?? 0) / colli.quantity) * 100) / 100,
          loadMeter: Math.round((+(colli.loadMeter ?? 0) / colli.quantity) * 100) / 100,
          height: Math.round(+(colli.height ?? 0) * 100) / 100,
          length: Math.round(+(colli.length ?? 0) * 100) / 100,
          width: Math.round(+(colli.width ?? 0) * 100) / 100,
          type: colli.type,
          colliTypeRef: colli.colliTypeRef,
        }
        if (col.length > 0 && col.width > 0) {
          if (col.loadMeter == 0) {
            col.loadMeter = col.length * col.width / 2.4;
          }
          if (col.height > 0) {
            if (col.volume == 0) {
              col.volume = col.length * col.width * col.height;
            }
          }
        }

        cols.push(col)
      }
    }
    return cols
  }

  GetGroupedShipmentById(shipmentGroupedId: string) {
    return this.httpClient.post<ArchiveGroupShipmentResponse>(`${environment.apiUrl}/e/shipments/GetGroupShipmentById`, { GroupedShipmentId: shipmentGroupedId })
  }

  UpdateShipmentPrice(shipment: Shipment) {
    return this.httpClient.post<StandarResponse>(`${environment.apiUrl}/e/shipments/updateshipmentpricebyshipmentid`,
      {
        ShipmentId: shipment.id,
        ClientId: null,
        Price: (shipment as any).price as ShipmentPrice,
        PriceVaild: ((shipment as any).price as ShipmentPrice).priceValid,
        TotalPrice: ((shipment as any).price as ShipmentPrice).totalPrice,
        Currency: ((shipment as any).price as ShipmentPrice).currency,
        PriceLines: ((shipment as any).price as ShipmentPrice).priceLines


      } as UpdateShipmentPriceRequest);
  }

  ConsolidateShipments(mainShipmentId: string, shipmentIds: string[]) {
    return this.httpClient.post(environment.apiUrl + '/e/shipments/consolidate', { mainShipmentId: mainShipmentId, shipments: shipmentIds });
  }

  SearchArchiveV3VisShipmentService(formData: any, isFirstPage = false) {
    return this.httpClient.post<ArchiveSearchResponseRoot>(environment.apiUrl + '/e/shipments/getshipmentforarchive', formData).subscribe(x => {
      let result = new Array<ArchiveSearchObject>();
      x.items.forEach(y => {
        result.push({
          id: y.id,
          bookingDate: y.bookingDate,
          bookingSource: y.bookingSource,
          eventDate: y.ReadyDate,
          recipientName: y.recipientName,
          reference1: y.reference1,
          senderName: y.senderName,
          status: y.status,
          waybillNo: y.waybillNo,
        } as ArchiveSearchObject);
      });
      this.archiveResultSet$.next(result);
    })

    // .pipe(map((x, i) => {
    //   return {
    //     id: x.items[i].id,
    //     bookingDate: x.items[i].bookingDate,
    //     bookingSource: x.items[i].bookingSource,
    //     eventDate: x.items[i].ReadyDate,
    //     recipientName: x.items[i].recipientName,
    //     reference1: x.items[i].Reference1,
    //     senderName: x.items[i].SenderName,
    //     status: x.items[i].status,
    //     waybillNo: x.items[i].waybillNo,
    //   } as ArchiveSearchObject
    // } ) ).subscribe(x => this.archiveResultSet$.next(x))
  }
}
export class Guid {
  static newGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0,
        v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}


export interface ICreateShipmentResponse {
  trackingNumber: string;
}

export interface ShipmentArchiveQuery {
  barcode?: string;
  waybillNo?: string;
  trackAndTrace?: string;
  reference1?: string;
  reference2?: string;
  reference3?: string;
  fromDate?: string;
  toDate?: string;
  service?: string;
  senderName?: string;
  senderZip?: string;
  recipientName?: string;
  recipientZip?: string;
  deliveryDate?: string;
  note?: string;
}

export enum PriceFeatchStatus {
  ok = 0,
  unknown = 1,
  zonenotfound = 2,
  intervalnotfound = 4,
  collitypeNotFound = 8,
  collitypeTranslation = 16,
  customerhasnopricesheets = 32,
  customerhasnoproductprice = 64,
  unabletofindcustomer = 128,
  productnotfound = 256,
  nopricesheetprice = 512,
  nopricesheetpricerecalcldm = 1024,
  defaultcollitypeNotFound = 2048,
  nocustomerclientobject = 4096,
  customernotfound = 8192,
  creditornotfound = 16384,
}

export interface MarkShipmentAsReadyCommand {
  ShipmentId: string;
  ReadyAt: Date
  Message: string;
  SendMail: boolean;
  GroupedShipmentId: string;
}

export interface ArchiveSearchObject {
  bookingDate: Date | string;
  bookingSource: string;
  eventDate: Date | string;
  id: string;
  recipientName: string;
  reference1: string;
  senderName: string;
  status: string;
  waybillNo: string;
  __typename: string; // "ShipmentArchiveV2"
}

export interface ArchiveGroupShipmentResponse {
  items: ArchiveGroupShipmentObject[];
  errors: any[]
}

export interface ArchiveGroupShipmentObject {
  bookingDate: Date | string;
  bookingSource: string;
  carrier: string;
  eventDate: Date | string;
  hasDangerousGoods: boolean;
  id: string;
  pickupDate: string;
  readyDate: string;
  recipientName: string;
  reference1: string;
  senderName: string;
  status: string;
  totalWeight: number;
  waybillNo: string;
}

interface UpdateShipmentPriceRequest {
  ShipmentId: string; // Guid
  ClientId: string; // Guid => optianol
  Price: ShipmentPrice;

}

export interface StandarResponse {
  success: boolean;
}

interface ArchiveSearchResponse {

  Barcode: string;
  WaybillNo: string;
  TrackAndTrace: string;
  Reference1: string;
  Reference2: string;
  Reference3: string;
  FromDate: string;
  ToDate: string;
  Service: string;
  SenderName: string;
  SenderZip: string;
  RecipientName: string;
  RecipientZip: string;
  DeliveryDate: string;
  Note: string;
}

interface ArchiveSearchResponseRoot {
  items: SeachItem[];
  totalCount: number;
}

interface SeachItem {
  id: string;
  waybillNo: string;
  recipientZip: string;
  recipientName: string;
  hasDangerousGoods: string;
  ddOmex: string;
  collis: any[];
  clientNo: string;
  bookingSource: string;
  clientName: string;
  bookingDate: string;
  ReadyDate: Date;
  status: string;
  services: string[];
  TenantOwner: string;
  ShipmentAdmin: string;
  reference1: string;
  senderName: string;
  SenderZip: string;
}